import {
  Card,
  Col,
  Collapse,
  Divider,
  Flex,
  Row,
  Space,
  Typography,
} from "antd";
import { useTranslate, useGo } from "@refinedev/core";
import { Modal } from "@libertify/ui";
import { colors } from "@libertify/ui/theme-v2";
import type { ExperienceInsight } from "../../types";
import { getInsightProjectIds } from "../../helpers/insights";
import { InsightActionButtons } from "./InsightActionButtons";
import { InsightCommentSection } from "./InsightCommentSection";
import { InsightMetricCard } from "./InsightMetricCard";
import { InsightPriorityTag } from "./InsightPriorityTag";
import { InsightCategoryTag } from "./InsightCategoryTag";
import type { StatCardColor } from "pages/media/components/analytics/analyticsTheme";
import styled from "styled-components";
import { formatMetricValue } from "./utils";
import { useAntTheme } from "hooks/useAntTheme";

const METRIC_COLORS: StatCardColor[] = ["red", "green", "amber", "blue"];

export type InsightDetailModalProps = {
  insight: ExperienceInsight | null;
  organizationId: string;
  // Set on the project page: hides the related-experiences links (they'd point back here)
  projectId?: string;
  projectTitleById: Record<string, string>;
  open: boolean;
  onClose: () => void;
  onComplete?: (id: string) => void;
  onPostpone?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onReport?: (id: string) => void;
};

export const InsightDetailModal = ({
  insight,
  organizationId,
  projectId,
  projectTitleById,
  open,
  onClose,
  onComplete,
  onPostpone,
  onDismiss,
  onReport,
}: InsightDetailModalProps) => {
  const t = useTranslate();
  const go = useGo();
  const { theme } = useAntTheme();

  if (!insight) return null;

  const id = insight.id ?? "";

  const projectIds = projectId ? [] : getInsightProjectIds(insight);

  const metricsGrid = insight.metrics.slice(0, 4);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="medium"
      showFooter={false}
      style={{ margin: 20 }}
    >
      <Flex vertical gap={16}>
        <Space size={8}>
          <InsightPriorityTag priority={insight.priority} />
          <InsightCategoryTag category={insight.category} />
        </Space>
        <Typography.Title level={2}>{insight.action}</Typography.Title>

        {projectIds.length > 0 && (
          <Flex vertical gap={4}>
            <Collapse
              defaultActiveKey={projectIds.length === 1 ? ["1"] : []}
              ghost
              items={[
                {
                  key: "1",
                  label: t("media.analytics.insights.experienceLabel"),
                  children: (
                    <Flex vertical gap={4}>
                      {projectIds.map((projectId) => (
                        <Typography.Link
                          key={projectId}
                          href={
                            go({
                              to: `/media/projects/${projectId}/analytics`,
                              type: "path",
                            }) as string
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {projectTitleById[projectId] ?? projectId}
                        </Typography.Link>
                      ))}
                    </Flex>
                  ),
                },
              ]}
            />
          </Flex>
        )}

        <Card>
          <CardTitle level={5}>
            {t("media.analytics.insights.whatToDo")}
          </CardTitle>
          <Typography.Text>{insight.detailed_action}</Typography.Text>
        </Card>

        <Card>
          <CardTitle level={5}>
            {t("media.analytics.insights.whySuggest")}
          </CardTitle>
          <Typography.Text
            italic
            style={{ color: theme.colorGrey, fontSize: 13 }}
          >
            {insight.rationale}
          </Typography.Text>
          {metricsGrid.length > 0 && (
            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              {metricsGrid.map((metric, index) => (
                <Col span={12} key={metric.key}>
                  <InsightMetricCard
                    value={formatMetricValue(metric)}
                    label={metric.label}
                    color={
                      METRIC_COLORS[index % METRIC_COLORS.length] ?? "green"
                    }
                  />
                </Col>
              ))}
            </Row>
          )}
        </Card>

        <Flex vertical gap={8}>
          <Typography.Text strong>
            {t("media.analytics.insights.updateStatus")}
          </Typography.Text>
          <InsightActionButtons
            insightId={id}
            compact={false}
            value={insight.task?.state}
            onComplete={onComplete}
            onPostpone={onPostpone}
            onDismiss={onDismiss}
            onReport={onReport}
          />
        </Flex>
        <Divider style={{ margin: 0 }} />
        <InsightCommentSection
          insightId={id}
          organizationId={organizationId}
          projectId={projectId}
        />
      </Flex>
    </Modal>
  );
};

const CardTitle = styled(Typography.Title)`
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.06em !important;
  color: ${colors.text.secondary} !important;
  margin: 0;
  margin-bottom: 10px;
`;
